import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { useDispatch } from 'store';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'jsx/components/utilities/Loader';
import AuthService from 'services/AuthService';
import PasswordService from 'services/PasswordService';
import Authenticator from 'services/Authenticator';
import EmployerService from 'services/EmployerService';
import EmployeeService from 'services/EmployeeService';
import AdminService from 'services/AdminService';
import { getProfile } from 'store/slices/user';

// assets
import CompanyIcon from '../assets/images/settings/company_default.png'

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

const extractEmployeeData = (data) => {
    return {
        companyUUID: data.companyUUID,
        employeeUUID: data.employeeUUID,
        name: data.firstname + (data.lastname ? ' ' + data.lastname : ''),
        logo: data.pic,
        designation: data.designation?.name,
        isAdmin: data.admin ? 1 : 0,
        isManager: data.isManager ? 1 : 0
    };
};

const extractEmployerData = (data) => {
    return {
        name: data.name,
        email: data.contactEmail,
        logo: data.logo ? data.logo : CompanyIcon,
        designation: data.industry,
        companyUUID: data.uuid,
        adminUUID: data.companyAdminUserUUID,
        isAdmin: data.admin ? 1 : 0,
        isManager: data.isManager ? 1 : 0
    };
};

const extractAdminData = (data) => {
    return {
        uuid: data.userUUID,
        usertype: data.userType,
        name: data.firstname + (data.lastname ? ' ' + data.lastname : ''),
        logo: data.pic,
        email: data.email,
        phone: data.phone,
        designation: data.designation?.name,
        isAdmin: 1,
        isManager: 0
    };
};
const extractCsTechnicaldata = (data) => {
    return {
        uuid: data.userUUID,
        usertype: data.userType,
        name: data.firstname + (data.lastname ? ' ' + data.lastname : ''),
        logo: data.pic,
        email: data.email,
        phone: data.phone,
        designation: data.designation?.name,
        isAdmin: 1,
        isManager: 0
    };
};
const extractCsOthersdata = (data) => {
    return {
        uuid: data.userUUID,
        usertype: data.userType,
        name: data.firstname + (data.lastname ? ' ' + data.lastname : ''),
        logo: data.pic,
        email: data.email,
        phone: data.phone,
        designation: data.designation?.name,
        isAdmin: 1,
        isManager: 0
    };
};
const extractCadata = (data) => {
    return {
        uuid: data.userUUID,
        usertype: data.userType,
        name: data.firstname + (data.lastname ? ' ' + data.lastname : ''),
        logo: data.pic,
        email: data.email,
        phone: data.phone,
        designation: data.designation?.name,
        isAdmin: 1,
        isManager: 0
    };
};

const setEmployerProfile = async () => {
    let profile = await EmployerService.getBasicProfile();
    profile = extractEmployerData(profile.data);
    return profile;
};

const setEmployeeProfile = async () => {
    let profile = await EmployeeService.listCompanies();
    profile = extractEmployeeData(profile.data[0]);
    return profile;
};

const setAdminProfile = async () => {
    let profile = await AdminService.getAuthProfile();
    profile = extractAdminData(profile.data);
    return profile;
};

const setCSTechnicalProfile = async () => {
    let profile = await AdminService.getAuthProfile();
    profile = extractCsTechnicaldata(profile.data);
    return profile;
};
const setCSOthersProfile = async () => {
    let profile = await AdminService.getAuthProfile();
    profile = extractCsOthersdata(profile.data);
    return profile;
};
const setCaProfile = async () => {
    let profile = await AdminService.getAuthProfile();
    profile = extractCadata(profile.data);
    return profile;
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

const userData = async (dispatch, dispatchRedux) => {
    try {
        if (!Authenticator.isAuthenticated()) {
            console.error('Not Authenticated, Please Login!');
            dispatch({
                type: LOGOUT
            });
            return;
        }
        let profile = null;
        if (localStorage.getItem('role') === 'employer') {
            profile = await setEmployerProfile();
        } else if (localStorage.getItem('role') === 'employee') {
            profile = await setEmployeeProfile();
        } else if (localStorage.getItem('role') === 'admin') {
            profile = await setAdminProfile();
        } else if (localStorage.getItem('role') === 'cs-technical') {
            profile = await setCSTechnicalProfile();
        } else if (localStorage.getItem('role') === 'cs-others') {
            profile = await setCSOthersProfile();
        } else if (localStorage.getItem('role') === 'ca') {
            profile = await setCaProfile();
        } else {
            console.error('Invalid role');
            dispatch({
                type: LOGOUT
            });
            return;
        }
        dispatchRedux(getProfile(profile));
        const user = {
            role: localStorage.getItem('role'),
            profile
        };
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user
            }
        });
    } catch (err) {
        console.error(err);
        dispatch({
            type: LOGOUT
        });
    }
};

export const JWTProvider = ({ children }) => {

    const [state, dispatch] = useReducer(accountReducer, initialState);

    const dispatchRedux = useDispatch();

    useEffect(() => {
        userData(dispatch, dispatchRedux);
    }, []);

    const setToken = async (data) => {
        const user = data.account;
        localStorage.setItem('access_token', data.access_token);
        let profile = null;
        switch (user.user_type) {
            case 'company':
                user.role = 'employer';
                localStorage.setItem('role', 'employer');
                profile = await setEmployerProfile();
                break;
            case 'employee':
                user.role = 'employee';
                localStorage.setItem('role', 'employee');
                profile = await setEmployeeProfile();
                profile.uuid = user.uuid;
                break;
            case 'admin':
                user.role = 'admin';
                localStorage.setItem('role', 'admin');
                profile = await setAdminProfile();
                profile.uuid = user.uuid;
                break;
            case 'cs-technical':
                user.role = 'cs-technical';
                localStorage.setItem('role', 'cs-technical');
                profile = await setCSTechnicalProfile();
                profile.uuid = user.uuid;
                break;
            case 'cs-others':
                user.role = 'cs-others';
                localStorage.setItem('role', 'cs-others');
                profile = await setCSOthersProfile();
                profile.uuid = user.uuid;
                break;
            case 'ca':
                user.role = 'ca';
                localStorage.setItem('role', 'ca');
                profile = await setCSOthersProfile();
                profile.uuid = user.uuid;
                break;
            default:
                console.error('Invalid user type');
                break;
        }
        return {
            profile, user
        };
    };

    const login = async (data) => {
        const res = await AuthService.login(data);
        if (res.data.reference) {
            return res;
        } else {
            const tokenResp = await setToken(res.data);
            dispatchRedux(getProfile(tokenResp.profile));
            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user: tokenResp.user
                }
            });
        }
    };

    const verifyOtp = async (data) => {
        const res = await AuthService.verifyOtp(data);
        const tokenResp = await setToken(res.data);
        dispatchRedux(getProfile(tokenResp.profile));
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user: tokenResp.user
            }
        });
    };

    const verifyUser = async (formData) => {
        const data = {
            companyname: formData.companyname,
            email: formData.email,
            recaptcha_token: formData.recaptcha_token,
            usertype: 'company'
        };
        const res = await AuthService.verifyUser(data);
        return res;
    };

    const register = async (formData) => {
        const data = {
            ...formData,
            usertype: 'company',
            firstname: formData['companyname']
        };
        const res = await AuthService.register(data);
        localStorage.setItem('access_token', res.data.access_token);
        return res;
    };

    const logout = () => {
        Authenticator.logout();
        dispatch({ type: LOGOUT });
    };

    const forgotPassword = async (email) => {
        await PasswordService.forgotPassword({ email: email });
        return;
    };

    const validResetPasswordToken = async (token) => {
        const res = await PasswordService.passwordToken({ token: token });
        return res;
    };

    const resetPassword = async (data) => {
        const res = await PasswordService.resetPassword(data);
        return res.data;
    };

    const updateProfile = () => { };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider
            value={{ ...state, login, logout, verifyUser, register, forgotPassword, resetPassword, validResetPasswordToken, verifyOtp, updateProfile }}
        >
            {children}
        </JWTContext.Provider>
    );

};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export { userData };

export default JWTContext;
